(() => {
  const PLAYER_CACHE_TTL = 5 * 60 * 60 * 1000;
  const purchaseStatus = window.Team10CartValidation?.purchaseStatus || {};

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || "en", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD",
    }).format(amount);
  };

  const getProductCardVariantId = (card) => {
    const productCard = card.querySelector("product-card");
    return productCard?.getSelectedVariantId?.() || card.dataset.variantId || "";
  };

  const getCardData = (card) => ({
    id: getProductCardVariantId(card),
    productId: card.dataset.productId || "",
    quantity: Math.max(1, Number(card.querySelector("[data-t10-qty]")?.value || 1) || 1),
    price: Number(card.dataset.price || 0),
    image: card.dataset.image || "",
    required: card.dataset.required === "true",
    purchased: card.dataset.purchased === "true",
    minimumQty: Math.max(1, Number(card.dataset.minimumQty || 1) || 1),
  });

  const cartProperties = (root, item) => ({
    _clubID: root.dataset.clubUid || "",
    _divisionID: root.dataset.divisionUid || "",
    _position: root.dataset.position || "",
    _requiredBadge: item.required ? "true" : "false",
    _minimumQty: item.minimumQty ? String(item.minimumQty) : "1",
    _customImg: item.image || "",
    _playerID: root.dataset.playerId || "",
    _playerUID: root.dataset.playerUid || "",
    _childPlayerID: root.dataset.childPlayerId || "",
    _childPlayerUID: root.dataset.childPlayerUid || "",
    _playerName: root.dataset.playerName || "",
    _jerseyNumber: root.dataset.jerseyNumber || "",
    "Player Name": root.dataset.playerName || "",
    "Jersey Number": root.dataset.jerseyNumber || "",
  });

  const activeShopperKey = (root) => {
    if (root.dataset.childPlayerId || root.dataset.childPlayerUid) {
      return `child:${root.dataset.childPlayerId || root.dataset.childPlayerUid}`;
    }
    return `player:${root.dataset.playerId || root.dataset.playerUid || window.Team10CartValidation?.customerId || ""}`;
  };

  const activeShopperStatus = (root) => {
    const clubStatus = purchaseStatus?.clubs?.[root.dataset.clubUid] || {};
    return clubStatus.players?.[activeShopperKey(root)] || {};
  };

  const shopperKeyFromProperties = (properties = {}) => {
    if (properties._childPlayerID || properties._childPlayerUID) {
      return `child:${properties._childPlayerID || properties._childPlayerUID}`;
    }
    return `player:${properties._playerID || properties._playerUID || window.Team10CartValidation?.customerId || ""}`;
  };

  const cartHasRequiredItemForActivePlayer = (root, item, cart) => {
    if (!item.required || !cart?.items?.length) return false;
    const currentShopperKey = activeShopperKey(root);
    return cart.items.some((cartItem) => {
      const properties = cartItem.properties || {};
      return String(cartItem.product_id || "") === String(item.productId || "")
        && String(properties._clubID || "") === String(root.dataset.clubUid || "")
        && String(properties._divisionID || properties._clubID || "") === String(root.dataset.divisionUid || root.dataset.clubUid || "")
        && shopperKeyFromProperties(properties) === currentShopperKey;
    });
  };

  const filterItemsAlreadyInCart = (root, items, cart) => items.filter((item) => (
    !cartHasRequiredItemForActivePlayer(root, item, cart)
  ));

  const fetchCart = () => fetch("/cart.js").then((cartResponse) => cartResponse.json());

  const updateAlreadyInCartState = async (root) => {
    try {
      const cart = await fetchCart();
      root.querySelectorAll("[data-t10-product-card]").forEach((card) => {
        const item = getCardData(card);
        const alreadyInCart = cartHasRequiredItemForActivePlayer(root, item, cart);
        card.dataset.inCart = alreadyInCart ? "true" : "false";
      });
      updatePurchasedState(root, { skipCartSync: true });
    } catch (error) {
      console.error(error);
    }
  };

  const isPurchasedForActivePlayer = (root, card) => {
    const status = activeShopperStatus(root);
    const productId = String(card.dataset.productId || "");
    const variantId = String(card.dataset.variantId || "");
    return status.clubAccessUnlocked === true
      || status.mandatoryBundlePurchased === true
      || status.purchasedProducts?.[productId] === true
      || (status.purchasedProductIds || []).map(String).includes(productId)
      || status.purchasedVariants?.[variantId] === true
      || (status.purchasedVariantIds || []).map(String).includes(variantId);
  };

  const selectedItems = (root) => Array.from(root.querySelectorAll("[data-t10-product-card]"))
    .filter((card) => card.querySelector("[data-t10-select]")?.checked)
    .map(getCardData)
    .filter((item) => item.id);

  const addableSelectedItems = (root) => Array.from(root.querySelectorAll("[data-t10-product-card]"))
    .filter((card) => card.querySelector("[data-t10-select]")?.checked)
    .filter((card) => !(card.dataset.required === "true" && card.dataset.inCart === "true"))
    .map(getCardData)
    .filter((item) => item.id);

  const requiredItems = (root) => Array.from(root.querySelectorAll('[data-t10-product-card][data-required="true"]'))
    .map(getCardData)
    .filter((item) => item.id);

  const missingRequiredItems = (root) => requiredItems(root).filter((item) => !item.purchased);

  const persistRequiredMap = (root) => {
    const required = requiredItems(root).map((item) => ({
      productId: item.productId,
      variantId: item.id,
      minimumQty: item.minimumQty,
      image: item.image || "",
      title: root.querySelector(`[data-t10-product-card][data-product-id="${item.productId}"]`)?.dataset.productTitle || "Required item",
    }));
    if (!required.length) return;
    const existing = JSON.parse(localStorage.getItem("team10-required-products") || "{}");
    existing[root.dataset.divisionUid || root.dataset.clubUid] = required;
    localStorage.setItem("team10-required-products", JSON.stringify(existing));
  };

  const playerStorageKey = (root) => `team10-player:${root.dataset.divisionUid || root.dataset.clubUid}`;

  const readStoredPlayer = (root) => {
    try {
      const pending = JSON.parse(sessionStorage.getItem("team10-pending-player") || "null");
      if (pending?.path === window.location.pathname) {
        sessionStorage.removeItem("team10-pending-player");
        return pending;
      }
      const stored = JSON.parse(localStorage.getItem(playerStorageKey(root)) || "null");
      if (!stored?.expiresAt || stored.expiresAt < Date.now()) {
        localStorage.removeItem(playerStorageKey(root));
        return null;
      }
      return stored;
    } catch {
      sessionStorage.removeItem("team10-pending-player");
      localStorage.removeItem(playerStorageKey(root));
      return null;
    }
  };

  const syncPlayerInputs = (root) => {
    document.querySelectorAll('input[name="properties[_playerName]"]').forEach((input) => {
      input.value = root.dataset.playerName || "";
    });
    document.querySelectorAll('input[name="properties[_jerseyNumber]"]').forEach((input) => {
      input.value = root.dataset.jerseyNumber || "";
    });
    document.querySelectorAll('input[name="properties[Player Name]"]').forEach((input) => {
      input.value = root.dataset.playerName || "";
    });
    document.querySelectorAll('input[name="properties[Jersey Number]"]').forEach((input) => {
      input.value = root.dataset.jerseyNumber || "";
    });
  };

  const setActivePlayer = (root, player = {}, persist = true) => {
    const name = player.name || "";
    const jersey = player.jersey || "";
    root.dataset.playerName = name || root.dataset.playerName || "";
    root.dataset.jerseyNumber = jersey || root.dataset.jerseyNumber || "";
    root.dataset.playerId = player.playerId || root.dataset.playerId || "";
    root.dataset.playerUid = player.playerUid || root.dataset.playerUid || "";
    root.dataset.childPlayerId = player.childPlayerId || "";
    root.dataset.childPlayerUid = player.childPlayerUid || "";

    const activeName = root.querySelector("[data-t10-active-player-name]");
    const activeJersey = root.querySelector("[data-t10-active-jersey]");
    if (activeName) activeName.textContent = root.dataset.playerName;
    if (activeJersey) activeJersey.textContent = root.dataset.jerseyNumber;

    syncPlayerInputs(root);
    updatePurchasedState(root);
    if (persist) {
      localStorage.setItem(playerStorageKey(root), JSON.stringify({
        name: root.dataset.playerName,
        jersey: root.dataset.jerseyNumber,
        playerId: root.dataset.playerId,
        playerUid: root.dataset.playerUid,
        childPlayerId: root.dataset.childPlayerId,
        childPlayerUid: root.dataset.childPlayerUid,
        teamUid: player.teamUid || root.dataset.divisionUid || "",
        expiresAt: Date.now() + PLAYER_CACHE_TTL,
      }));
      root.dataset.playerSelectionCached = "true";
    }
  };

  const updatePurchasedState = (root, options = {}) => {
    root.querySelectorAll("[data-t10-product-card]").forEach((card) => {
      const purchased = isPurchasedForActivePlayer(root, card);
      const inCart = card.dataset.inCart === "true";
      card.dataset.purchased = purchased ? "true" : "false";
      const label = card.querySelector(".t10-bundle-check");
      const input = card.querySelector("[data-t10-select]");
      const text = label?.querySelector("span");
      const required = card.dataset.required === "true";
      label?.classList.toggle("t10-bundle-check--purchased", purchased);
      label?.classList.toggle("t10-bundle-check--in-cart", inCart && !purchased);
      if (text) text.textContent = purchased ? "Already Purchased" : (inCart ? "Already in cart" : (required ? "Required" : "Optional"));
      if (input) {
        input.disabled = required && !purchased;
        if (required && !purchased) input.checked = true;
      }
      normalizeCardQty(card);
    });
    refreshBar(root);
    if (!options.skipCartSync) updateAlreadyInCartState(root);
  };

  const sameCollectionPlayer = (root, option) => {
    const optionPath = option.dataset.url ? new URL(option.dataset.url, window.location.origin).pathname : "";
    const teamUid = option.dataset.teamUid || "";
    const optionPosition = (option.dataset.position || "").toUpperCase();
    const rootPosition = (root.dataset.position || "").toUpperCase();
    const isAdditionalPlayer = Boolean(option.dataset.childPlayerId || option.dataset.childPlayerUid);

    if (!isAdditionalPlayer) return true;
    if (teamUid && root.dataset.divisionUid && teamUid !== root.dataset.divisionUid) return false;
    if (optionPosition && rootPosition && optionPosition !== rootPosition) return false;
    if (!teamUid && optionPath) return optionPath === window.location.pathname;
    return true;
  };

  const showDivisionMismatch = (root) => {
    const modal = root.querySelector("[data-t10-player-error-modal]");
    const message = root.querySelector("[data-t10-player-error-message]");
    if (message) {
      message.textContent = root.dataset.divisionMismatchMessage
        || "The selected additional player belongs to a different division/position, so you cannot purchase items from this collection. Please contact us for help with a manual purchase.";
    }
    modal?.showModal?.();
  };

  const handlePlayerOption = (root, option) => {
    if (!sameCollectionPlayer(root, option)) {
      root.querySelector("[data-t10-player-modal]")?.close?.();
      showDivisionMismatch(root);
      return;
    }
    setActivePlayer(root, {
      name: option.dataset.name || "",
      jersey: option.dataset.jersey || "",
      playerId: option.dataset.playerId || "",
      playerUid: option.dataset.playerUid || "",
      childPlayerId: option.dataset.childPlayerId || "",
      childPlayerUid: option.dataset.childPlayerUid || "",
      teamUid: option.dataset.teamUid || "",
    });
    root.querySelector("[data-t10-player-modal]")?.close?.();
  };

  const addItems = async (root, items) => {
    const existingCart = await fetchCart();
    const addableItems = filterItemsAlreadyInCart(root, items, existingCart);
    if (!addableItems.length) return { cart: existingCart, addedItems: [] };

    const response = await fetch(Theme.routes.cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: addableItems.map((item) => ({
          id: Number(item.id),
          quantity: item.quantity,
          properties: cartProperties(root, item),
        })),
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || "Unable to add selected products.");
    }

    const cart = await fetchCart();
    document.dispatchEvent(new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: "team10-bulk-add",
        data: {
          source: "team10-purchasing-flow",
          itemCount: addableItems.reduce((sum, item) => sum + item.quantity, 0),
        },
      },
    }));

    return { cart, addedItems: addableItems };
  };

  const animateFlyToCart = (button, image) => {
    const cartIcon = document.querySelector(".header-actions__cart-icon");
    if (!cartIcon || !button || !image) return;

    const flyToCart = document.createElement("fly-to-cart");
    flyToCart.classList.add("fly-to-cart--sticky", "t10-fly-to-cart");
    flyToCart.style.setProperty("background-image", `url(${image})`);
    flyToCart.style.setProperty("--start-opacity", "0");
    flyToCart.source = button;
    flyToCart.destination = cartIcon;
    document.body.appendChild(flyToCart);
  };

  const refreshBar = (root) => {
    const items = addableSelectedItems(root);
    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const count = items.reduce((sum, item) => sum + item.quantity, 0);
    const bar = root.querySelector("[data-t10-bundle-bar]");

    root.querySelector("[data-t10-selected-count]").textContent = String(count);
    root.querySelector("[data-t10-total-price]").textContent = formatMoney(total);
    root.querySelector("[data-t10-bulk-atc]").disabled = items.length === 0;
    bar?.toggleAttribute("data-empty", items.length === 0);
  };

  const updateCardVariant = (card, option) => {
    card.dataset.variantId = option.dataset.variantId || card.dataset.variantId;
    card.dataset.price = option.dataset.price || card.dataset.price;
    card.dataset.image = option.dataset.image || card.dataset.image;
    card.querySelector("[data-t10-variant-label]").textContent = option.dataset.label || option.textContent.trim();
    const cardButton = card.querySelector("[data-t10-card-atc]");
    if (cardButton) cardButton.disabled = option.dataset.available === "false";
    card.querySelectorAll("[data-t10-variant-option]").forEach((item) => {
      item.setAttribute("aria-selected", item === option ? "true" : "false");
    });
    closeVariantMenu(card.querySelector("[data-t10-variant-picker]"));
  };

  const closeVariantMenu = (picker) => {
    if (!picker) return;
    const trigger = picker.querySelector("[data-t10-variant-trigger]");
    const menu = picker.querySelector("[data-t10-variant-menu]");
    if (menu) menu.hidden = true;
    if (trigger) trigger.setAttribute("aria-expanded", "false");
  };

  const closeOtherVariantMenus = (root, activePicker) => {
    root.querySelectorAll("[data-t10-variant-picker]").forEach((picker) => {
      if (picker !== activePicker) closeVariantMenu(picker);
    });
  };

  const normalizeCardQty = (card) => {
    const input = card.querySelector("[data-t10-qty]");
    if (!input) return;
    const data = getCardData(card);
    const minimum = data.required && !data.purchased ? data.minimumQty : 1;
    input.min = String(minimum);
    input.value = String(Math.max(minimum, Number(input.value || minimum) || minimum));
  };

  const applyClubImages = (root) => {
    root.querySelectorAll("[data-t10-product-card][data-image]").forEach((card) => {
      const image = card.dataset.image;
      if (!image) return;
      const img = card.querySelector("product-card img");
      if (!img) return;
      img.src = image;
      img.removeAttribute("srcset");
    });
  };

  const itemsForCardAdd = (root, card) => {
    const target = getCardData(card);
    if (!target.id) return [];
    if (target.required || missingRequiredItems(root).length === 0) return [target];

    const items = missingRequiredItems(root).filter((item) => !cartHasRequiredItemForActivePlayer(root, item, {
      items: Array.from(root.querySelectorAll('[data-t10-product-card][data-in-cart="true"]')).map((card) => ({
        product_id: card.dataset.productId,
        properties: cartProperties(root, getCardData(card)),
      })),
    }));
    if (!items.some((item) => item.id === target.id)) items.push(target);
    return items;
  };

  const initPlayerModal = (root) => {
    const modal = root.querySelector("[data-t10-player-modal]");
    if (!modal) return;

    const stored = readStoredPlayer(root);
    if (stored) {
      setActivePlayer(root, stored, false);
      root.dataset.playerSelectionCached = "true";
    } else if (root.dataset.hasChildPlayers === "true") {
      modal.showModal?.();
    }

    root.querySelector("[data-t10-change-player]")?.addEventListener("click", () => {
      modal.showModal?.();
    });

    root.querySelector("[data-t10-close-player]")?.addEventListener("click", () => {
      modal.close?.();
    });

    root.querySelectorAll("[data-t10-close-player-error]").forEach((button) => {
      button.addEventListener("click", () => {
        root.querySelector("[data-t10-player-error-modal]")?.close?.();
        modal.showModal?.();
      });
    });

    root.querySelectorAll("[data-t10-player-option]").forEach((option) => {
      option.addEventListener("click", () => {
        handlePlayerOption(root, option);
      });
    });
  };

  const initFlow = (root) => {
    let cartStateTimer;
    const scheduleAlreadyInCartSync = () => {
      window.clearTimeout(cartStateTimer);
      cartStateTimer = window.setTimeout(() => updateAlreadyInCartState(root), 120);
    };

    initPlayerModal(root);
    syncPlayerInputs(root);
    root.querySelectorAll("[data-t10-product-card]").forEach(normalizeCardQty);
    applyClubImages(root);
    persistRequiredMap(root);
    updatePurchasedState(root);

    const applyPlayerInfo = (info) => {
      const player = info?.player;
      if (!player) return;
      if (root.dataset.playerSelectionCached !== "true") {
        setActivePlayer(root, {
          name: player.fullName || [player.firstName, player.lastName].filter(Boolean).join(" "),
          jersey: player.jerseyNumber || "",
          playerId: player.id || "",
          playerUid: player.uid || "",
        }, false);
      }
      const children = player.childPlayers || [];
      if (!children.length || root.querySelector("[data-t10-api-child='true']")) return;
      const list = root.querySelector(".t10-player-modal__list");
      if (!list) return;
      const changePlayerButton = root.querySelector("[data-t10-change-player]");
      if (changePlayerButton) changePlayerButton.hidden = false;
      children.forEach((child) => {
        const option = document.createElement("button");
        option.type = "button";
        option.className = "t10-player-option";
        option.dataset.t10PlayerOption = "";
        option.dataset.t10ApiChild = "true";
        option.dataset.name = child.fullName || [child.firstName, child.lastName].filter(Boolean).join(" ");
        option.dataset.jersey = child.jerseyNumber || "";
        option.dataset.playerId = player.id || "";
        option.dataset.playerUid = player.uid || "";
        option.dataset.childPlayerId = child.id || "";
        option.dataset.childPlayerUid = child.uid || "";
        option.dataset.teamUid = child.team?.uid || "";
        option.dataset.position = child.team?.position || "";
        option.dataset.url = child.team?.collection?.url || "";
        const name = document.createElement("strong");
        const jersey = document.createElement("span");
        name.textContent = option.dataset.name || "Additional player";
        jersey.textContent = `Jersey #${option.dataset.jersey || "-"}`;
        option.append(name, jersey);
        option.addEventListener("click", () => {
          handlePlayerOption(root, option);
        });
        list.appendChild(option);
      });
      if (root.dataset.hasChildPlayers !== "true" && root.dataset.playerSelectionCached !== "true") {
        root.dataset.hasChildPlayers = "true";
        root.querySelector("[data-t10-player-modal]")?.showModal?.();
      }
    };

    if (window.Team10PlayerInfo) applyPlayerInfo(window.Team10PlayerInfo);
    window.Team10PlayerInfoPromise?.then?.(applyPlayerInfo);

    const quickAddContent = document.getElementById("quick-add-modal-content");
    if (quickAddContent) {
      new MutationObserver(() => syncPlayerInputs(root)).observe(quickAddContent, {
        childList: true,
        subtree: true,
      });
    }

    root.addEventListener("change", (event) => {
      const card = event.target.closest?.("[data-t10-product-card]");
      if (card && event.target.matches("[data-t10-qty]")) normalizeCardQty(card);
      if (event.target.matches("[data-t10-select], [data-t10-qty]")) refreshBar(root);
    });

    root.addEventListener("click", async (event) => {
      if (event.target.closest(".t10-card-controls")) {
        event.stopPropagation();
      }

      const variantTrigger = event.target.closest("[data-t10-variant-trigger]");
      if (variantTrigger) {
        event.preventDefault();
        const picker = variantTrigger.closest("[data-t10-variant-picker]");
        const menu = picker?.querySelector("[data-t10-variant-menu]");
        if (menu) {
          const shouldOpen = menu.hidden;
          closeOtherVariantMenus(root, picker);
          menu.hidden = !shouldOpen;
          variantTrigger.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
        }
        return;
      }

      const variantOption = event.target.closest("[data-t10-variant-option]");
      if (variantOption) {
        event.preventDefault();
        updateCardVariant(variantOption.closest("[data-t10-product-card]"), variantOption);
        refreshBar(root);
        return;
      }

      const minus = event.target.closest("[data-t10-qty-minus]");
      const plus = event.target.closest("[data-t10-qty-plus]");
      if (minus || plus) {
        const card = event.target.closest("[data-t10-product-card]");
        const input = card?.querySelector("[data-t10-qty]");
        if (input) {
          input.value = String((Number(input.value || 1) || 1) + (plus ? 1 : -1));
          normalizeCardQty(card);
          refreshBar(root);
        }
        return;
      }

      const cardAtc = event.target.closest("[data-t10-card-atc]");
      if (cardAtc) {
        const card = cardAtc.closest("[data-t10-product-card]");
        const items = itemsForCardAdd(root, card);
        if (!items.length) return;

        cardAtc.disabled = true;
        try {
          const result = await addItems(root, items);
          if (result.addedItems.length) {
            animateFlyToCart(cardAtc, result.addedItems.find((item) => item.image)?.image || "");
          }
        } catch (error) {
          window.alert(error.message);
        } finally {
          cardAtc.disabled = false;
          await updateAlreadyInCartState(root);
          refreshBar(root);
        }
        return;
      }

      const bulk = event.target.closest("[data-t10-bulk-atc]");
      if (!bulk) return;

      const items = addableSelectedItems(root);
      if (!items.length) return;

      bulk.disabled = true;
      try {
        const result = await addItems(root, items);
        if (result.addedItems.length) {
          animateFlyToCart(bulk, result.addedItems.find((item) => item.image)?.image || "");
        }
        await updateAlreadyInCartState(root);
      } catch (error) {
        window.alert(error.message);
      } finally {
        bulk.disabled = false;
        refreshBar(root);
      }
    });

    document.addEventListener("click", (event) => {
      if (!root.contains(event.target)) return;
      if (event.target.closest("[data-t10-variant-picker]")) return;
      closeOtherVariantMenus(root, null);
    });

    document.addEventListener("team10:player-info", (event) => applyPlayerInfo(event.detail));
    document.addEventListener("cart:update", scheduleAlreadyInCartSync);

    new MutationObserver((mutations) => {
      const cartChanged = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target?.parentElement;
        if (target?.closest?.("cart-items-component, cart-drawer-component")) return true;
        return Array.from(mutation.addedNodes || []).some((node) => (
          node instanceof Element
          && (node.matches("cart-items-component, cart-drawer-component")
            || node.querySelector?.("cart-items-component, cart-drawer-component"))
        ));
      });
      if (cartChanged) scheduleAlreadyInCartSync();
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });

    refreshBar(root);
  };

  document.querySelectorAll("[data-team10-purchasing-flow]").forEach(initFlow);
})();
